import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { AppState } from '../../../../store/store.types';

import { PromoCodeActionTypes } from '../../types/promocode.types';
import { getPromoCodesList, UpdatePromoCodeFilters } from '../../actions/promocode.actions';
import { promoCodeEditRoles, PromoCodeFilters, PromoCodeModel } from '../../../../api/pricing/pricing.types';
import Select from '../../../../components/forms/Select';
import { BrokerRateModel, CarClassModel, DeskModel } from '../../../../api/core/core.types';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import DatePicker, { PromoCodesDatePickerEvent } from '../../../../components/forms/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import infoIcon from '../../../../assets/img/info-circle.svg';
import { getBrokerList } from '../../../admin/actions/broker.actions';
import { getBrokerRateList } from '../../../setup/actions/brokerrate.actions';
import { formatDate } from '../../../../helpers/date.helper';
import ListViewTable from '../../../../components/tables/ListViewTable';
import { BrokerModel, SupplierBrokerModel } from '../../../../api/admin/admin.types';
import { getSupplierBrokerList } from '../../../admin/actions/supplierbroker.actions';

interface PromoCodesProps extends RouteComponentProps, WithTranslation {
  getPromoCodesList: (filters?: PromoCodeFilters, page?: PageRequest) => void;
  getAllCarClasses: () => void;
  getDeskList: () => void;
  getBrokerList: () => void;
  getBrokerRateList: () => void;
  updateFilters: (filters: PromoCodeFilters) => void;
  promoCodes: PromoCodeModel[] | null;
  brokers: SupplierBrokerModel[];
  carClasses: CarClassModel[];
  brokerRates: BrokerRateModel[];
  desks: DeskModel[];
  user: UserModel | null;
  totalElements: number;
  totalPages: number;
  filters: PromoCodeFilters | null;
}

interface PromoCodeState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string | undefined;
  filtersUpdated: boolean;
  filter: {
    combinedFilter: {
      value: string;
    };
    pickupDateFilter: {
      value: Dayjs | undefined;
    };
    requestDateFilter: {
      value: Dayjs | undefined;
    };
    deskFilter: {
      value: [] | { label: string; value: string };
    };
    carClassFilter: {
      value: [] | { label: string; value: string };
    };
    statusFilter: {
      value: { label: string; value: string }[];
    };
  };
}

const PromoCodeSortFields = [
  { value: 'code', label: 'code' }
];

class PromoCodes extends React.Component<PromoCodesProps, PromoCodeState> {
  constructor(props: PromoCodesProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'DESC',
      sortedBy: 'code',
      filtersUpdated: false,
      filter: {
        combinedFilter: {
          value: ''
        },
        pickupDateFilter: {
          value: undefined
        },
        requestDateFilter: {
          value: undefined
        },
        deskFilter: {
          value: []
        },
        carClassFilter: {
          value: []
        },
        statusFilter: {
          value: []
        }
      }
    };
  }

  componentDidMount() {
    this.updateFilterValues();
    this.props.getAllCarClasses();
    this.props.getDeskList();
    this.props.getBrokerRateList();
    this.props.getBrokerList();
    this.props.getPromoCodesList(this.props.filters ?? undefined);
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  updateFilterValues = () => {
    this.setState({ filtersUpdated: true });
    const filters = this.props.filters;
    if (filters) {
      const desks = filters.desks ? filters.desks.split(',') : undefined;
      const cars = filters.carClasses ? filters.carClasses.split(',') : undefined;

      const selectedDeskOptions: { value: string; label: string }[] = [];
      if (desks && desks.length) {
        desks.forEach((sd) => {
          const desk = this.props.desks.find((d) => d.code === sd);
          if (desk) selectedDeskOptions.push({ value: desk.code, label: desk.name });
        });
      }

      const selectedCarClassOptions: { value: string; label: string }[] = [];
      if (cars && cars.length) {
        cars.forEach((sc) => {
          selectedCarClassOptions.push({ value: sc, label: sc });
        });
      }

      this.setState({
        selectedPageNumber: filters.pageNumber ? filters.pageNumber : 1,
        filtersUpdated: true,
        filter: {
          combinedFilter: {
            value: filters.combinedFilterField ? filters.combinedFilterField : ''
          },
          pickupDateFilter: {
            value: filters.pickupDate ? dayjs(filters.pickupDate) : undefined
          },
          requestDateFilter: {
            value: filters.requestDate ? dayjs(filters.requestDate) : undefined
          },
          deskFilter: {
            value: selectedDeskOptions[ 0 ]
          },
          carClassFilter: {
            value: selectedCarClassOptions[ 0 ]
          },
          statusFilter: {
            value: filters.status ? [ {
              value: filters.status,
              label: filters.status.charAt(0).toUpperCase() + filters.status.substring(1).toLowerCase()
            } ] : []
          }
        }
      });
    }
  };

  clearFilters = () => {
    this.setState({
      selectedPageNumber: 1,
      filter: {
        combinedFilter: {
          value: ''
        },
        pickupDateFilter: {
          value: undefined
        },
        requestDateFilter: {
          value: undefined
        },
        statusFilter: {
          value: []
        },
        deskFilter: {
          value: []
        },
        carClassFilter: {
          value: []
        }
      }
    });
    const filters = this.composePromoCodeFilters();
    this.props.getPromoCodesList();
    this.props.updateFilters(filters);
  };

  onRowClick = (rowInfo: any) => this.props.history.push(`/rates/promocodes/${rowInfo.original.code}/view`);

  discountCell = (props: any) => `${props * 100}%`;

  rentalDaysCell = (props: any) => {
    if (props.row.original.daysFrom !== null && props.row.original.daysTo !== null) {
      return (
        `${props.row.original.daysFrom} - ${props.row.original.daysTo}`
      );
    } else {
      return 'Any';
    }
  };

  pickupFromCell = (props: any) => (
      `${formatDate(props.row.original.pickupFrom)} - ${formatDate(props.row.original.pickupTo)}`
  );

  requestFromCell = (props: any) => (
      `${formatDate(props.row.original.requestFrom)} - ${formatDate(props.row.original.requestTo)}`
  );


  carClassCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.carClasses.length > 0) {
      const carList: JSX.Element[] = [];
      row.original.carClasses.forEach((carClass: any) => {
        const carObj = this.props.carClasses.find((c) => c.acrissCode === carClass.acrissCode);
        carList.push(
          <div
            key={carClass.acrissCode}
            className="flex-50 px-1"
            style={{ maxWidth: '60px' }}
          >
            {carObj ? carObj.acrissCode : carClass.acrissCode}
          </div>
        );
      });
      if (row.original.carClasses.length === 1) {
        const carObj = this.props.carClasses.find((d) => d.acrissCode === row.original.carClasses[ 0 ].acrissCode);
        return carObj && carObj.acrissCode || row.original.carClasses[ 0 ].acrissCode;
      }
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`cars-tooltip-${row.original.code}`}
          >
            {t('v.car_classes', { count: row.original.carClasses.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`cars-tooltip-${row.original.code}`}
            autohide={false}
          >
            <div className="d-flex flex-wrap">
              {carList}
            </div>
          </Tooltip>
        </div>
      );
    }
    return t('v.all_car_classes');
  };

  deskCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.desks.length > 0) {
      const deskList: JSX.Element[] = [];
      row.original.desks.forEach((desk: any) => {
        const deskObj = this.props.desks.find((d) => d.code === desk.deskCode);
        deskList.push(
          <div key={desk.deskCode}>{deskObj ? deskObj.name : desk.deskCode}</div>
        );
      });

      if (row.original.desks.length === 1) {
        const deskObj = this.props.desks.find((d) => d.code === row.original.desks[ 0 ].deskCode);
        return deskObj && deskObj.name || row.original.desks[ 0 ].deskCode;
      }
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`desks-tooltip-${row.original.code}`}
          >
            {t('v.desks', { count: row.original.desks.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`desks-tooltip-${row.original.code}`}
            autohide={false}
          >
            {deskList}
          </Tooltip>
        </div>
      );
    }
    return t('v.all_desks');
  };

  brokerCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.brokers.length === 1) {
      const brokerObj = this.props.brokers.find((d) => d.id === row.original.brokers[ 0 ].id);
      return brokerObj && brokerObj.name || row.original.brokers[ 0 ].id;
    }
    if (row.original.brokers.length > 0) {
      const brokerList: JSX.Element[] = [];
      row.original.brokers.forEach((broker: any) => {
        const brokerObj = this.props.brokers.find((c) => c.id === broker.id);
        brokerList.push(
          <div key={broker.id}>
            {brokerObj ? brokerObj.name : broker.id}
          </div>
        );
      });
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`brokers-tooltip-${row.original.code}`}
          >
            {t('v.promo_codes.brokers', { count: row.original.brokers.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`brokers-tooltip-${row.original.code}`}
            autohide={false}
          >
            {brokerList}
          </Tooltip>
        </div>
      );
    }
    return t('v.promo_codes.all_brokers');
  };

  brokerRateCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.brokerRates.length === 1) {
      const brokerObj = this.props.brokerRates.find((d) => d.code === row.original.brokerRates[ 0 ].brokerRateCode);
      return brokerObj && brokerObj.code || row.original.brokerRates[ 0 ].brokerRateCode;
    }
    if (row.original.brokerRates.length > 0) {
      const brokerRateList: JSX.Element[] = [];
      row.original.brokerRates.forEach((brokerRate: any) => {
        const brokerObj = this.props.brokerRates.find((c) => c.code === brokerRate.brokerRateCode);
        brokerRateList.push(
          <div key={brokerRate.code}>
            {brokerObj ? brokerObj.code : brokerRate.code}
          </div>
        );
      });
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`broker-rates-tooltip-${row.original.code}`}
          >
            {t('v.promo_codes.broker_rates', { count: row.original.brokerRates.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`broker-rates-tooltip-${row.original.code}`}
            autohide={false}
          >
            {brokerRateList}
          </Tooltip>
        </div>
      );
    }
    return t('v.promo_codes.all_broker_rates');
  };

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  onFilterClick = async (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composePromoCodeFilters();
    await this.props.getPromoCodesList(filters);
    this.props.updateFilters(filters);
  };

  triggerUpdate = async () => {
    const filters = this.composePromoCodeFilters();
    this.props.getPromoCodesList(filters);
    this.props.updateFilters(filters);
  };

  composePromoCodeFilters = () => {
    const pickupDate = this.state.filter.pickupDateFilter.value;
    const requestDate = this.state.filter.requestDateFilter.value;
    const status: any = this.state.filter.statusFilter.value;
    const deskFilterValue: any = this.state.filter.deskFilter.value;
    const carClassFilterValue: any = this.state.filter.carClassFilter.value;

    const filters: PromoCodeFilters = {
      combinedFilterField: this.state.filter.combinedFilter.value,
      pickupDate: pickupDate ? pickupDate.format('YYYY-MM-DD') : undefined,
      requestDate: requestDate ? requestDate.format('YYYY-MM-DD') : undefined,
      desks: deskFilterValue ? deskFilterValue.value : undefined,
      carClasses: carClassFilterValue ? carClassFilterValue.value : undefined,
      status: status ? status.value : undefined,
      pageNumber: this.state.selectedPageNumber - 1
    };

    return filters;
  };


  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string, isMulti: boolean) => {
    this.setState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      if (!isMulti) {
        this.triggerUpdate();
      }
    });
  };

  onDateChange = (field: PromoCodesDatePickerEvent, date?: Dayjs) => {
    this.setState({
      filter: {
        ...this.state.filter,
        [ field.valueOf() ]: {
          value: date
        }
      }
    });
  };

  getLORHeader = (headerName: string, headerComment: string) => {
    return (
      <div>
        <div>{headerName}
          <img src={infoIcon} className="i-info-icon" id="lor-header-tooltip"/>
        </div>
        <Tooltip
          className="dark-tooltip"
          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
          placement="right"
          target="lor-header-tooltip"
          autohide={false}
        >
          <div className="d-flex flex-wrap">
            <pre className="dark-tooltip-text">{headerComment}</pre>
          </div>
        </Tooltip>
      </div>
    );
  };

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.promo_codes.code'), accessorKey: 'code', id: 'code'
      },
      {
        header: t('ct.promo_codes.pickup'), accessorKey: 'pickupFrom', cell: (info: any) => this.pickupFromCell(info),
        meta: { align: 'right', numeric: true }
      },
      {
        header: t('ct.promo_codes.request'),
        accessorKey: 'requestFrom',
        cell: this.requestFromCell,
        meta: { align: 'right', numeric: true }
      },
      {
        header: t('ct.promo_codes.discount'),
        accessorKey: 'discountPercentage',
        cell: (info: any) => this.discountCell(info.getValue()),
        meta: { align: 'right', maxWidth:100, numeric: true }
      },
      {
        header: () => this.getLORHeader(t('ct.promo_codes.rental_days'), t('comment.promo_code.lor')),
        accessorKey: 'daysFrom',
        cell: (info: any) => this.rentalDaysCell(info),
        meta: { align: 'right', maxWidth:100, numeric: true }
      },
      {
        header: t('ct.promo_codes.car_classes'), accessorKey: 'carClasses', cell: this.carClassCell
      },
      {
        header: t('ct.promo_codes.desks'), accessorKey: 'desks', cell: this.deskCell
      },
      {
        header: t('ct.promo_codes.brokers'), accessorKey: 'brokers', cell: this.brokerCell
      },
      {
        header: t('ct.promo_codes.broker_rates'), accessorKey: 'brokerRates', cell: this.brokerRateCell
      },
      {
        header: t('ct.promo_codes.status'), accessorKey: 'statusType', cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = PromoCodeSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: sortFieldName.label });
    return (
      this.props.getPromoCodesList(this.composePromoCodeFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page,
        sortDirection: pageRequest.sortDirection,
        sortField: PromoCodeSortFields.find((column) => column.label === pageRequest.sortField)?.label
      };
      this.props.getPromoCodesList(this.composePromoCodeFilters(), pageRequestFix);

      this.setState({
        selectedPageNumber: pageRequestFix.page + 1,
        sortOrder: pageRequestFix.sortDirection ?? 'ASC',
        sortedBy: PromoCodeSortFields.find((column) => column.label === pageRequest.sortField)?.label
      });
    }
  }


  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.promo_codes')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, promoCodeEditRoles) && (
              <Link
                to={{
                  pathname: '/rates/promocodes/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="container-fluid filter-container form-group form-inline d-flex justify-content-between">
            <div className="d-flex justify-content-start align-items-center ml-1 filters-container flex-wrap">
              <input
                type="text"
                value={this.state.filter.combinedFilter.value}
                name="combinedFilter"
                className="form-control col filter-select margins filter-item-margin"
                placeholder={t('ph.filter.promo_codes.search')}
                onChange={this.changeHandler}
              />
              <div className="mr-1 filter-select filter-item-margin">
                <DatePicker
                  onChange={(val) => this.onDateChange(PromoCodesDatePickerEvent.PICKUP, val)}
                  isEdit
                  isFilter
                  date={this.state.filter.pickupDateFilter.value}
                  id="0"
                />
              </div>
              <div className="mr-1 filter-select filter-item-margin">
                <DatePicker
                  onChange={(val) => this.onDateChange(PromoCodesDatePickerEvent.REQUEST, val)}
                  isEdit
                  isFilter
                  date={this.state.filter.requestDateFilter.value}
                  id="1"
                />
              </div>
              <div className="margins-form-select ml-0">
                <Select
                  className="mr-1 filter-item-margin"
                  placeholder={t('ph.filter.promo_codes.by_car_class')}
                  options={this.props.carClasses.map((m) => ({ label: m.acrissCode, value: m.acrissCode }))}
                  value={this.state.filter.carClassFilter.value}
                  onChange={(val) => this.dropdownChange(val, 'carClassFilter', false)}
                  closeMenuOnSelect={false}
                  handleBlurChanged={() => {
                    this.triggerUpdate();
                  }}
                  isMulti={false}
                  hideSelectedOptions={false}
                  label="car class"
                />
              </div>
              <div className="margins-form-select ml-0">
                <Select
                  className="mr-1 filter-item-margin"
                  placeholder={t('ph.filter.promo_codes.by_desk')}
                  elWidth={250}
                  options={this.props.desks.map((d) => ({ label: d.name, value: d.code }))}
                  value={this.state.filter.deskFilter.value}
                  onChange={(val) => this.dropdownChange(val, 'deskFilter', false)}
                  closeMenuOnSelect={false}
                  handleBlurChanged={() => {
                    this.triggerUpdate();
                  }}
                  isMulti={false}
                  hideSelectedOptions={false}
                  label="location"
                />
              </div>
              <Select
                value={this.state.filter.statusFilter.value}
                equalWidthElement
                className="mr-1"
                marginTop
                placeholder={t('ph.filter.promo_codes.by_status')}
                options={[
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'INACTIVE', label: 'Inactive' },
                  { value: 'EXPIRED', label: 'Expired' }
                ]}
                onChange={(val) => this.dropdownChange(val, 'statusFilter', false)}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="status"
              />
            </div>
            <div className="pl-1">
              <button type="button" className="btn btn-text-only mr-1" onClick={this.clearFilters}>
                {t('b.clear_filter')}
              </button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}
              </button>
            </div>
          </form>
          {this.props.promoCodes
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.promoCodes}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={PromoCodeSortFields.find(({ value, label }) => this.state.sortedBy === label)?.value}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}/>
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  promoCodes: state.rates.promoCodes.promoCodes,
  carClasses: state.core.carClasses.acrissCodes,
  desks: state.setup.desks.desks,
  brokers: state.admin.supplierBrokers.supplierBrokers,
  brokerRates: state.core.brokerRates.brokerRates,
  user: state.authentication.user,
  totalPages: state.rates.promoCodes.totalPages,
  totalElements: state.rates.promoCodes.totalElements,
  filters: state.rates.promoCodes.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, PromoCodeActionTypes>) => ({
  getPromoCodesList: (filters?: PromoCodeFilters, page?: PageRequest) => dispatch(getPromoCodesList(filters, page)),
  getDeskList: () => dispatch(getDeskList()),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getBrokerList: () => dispatch(getSupplierBrokerList()),
  getBrokerRateList: () => dispatch(getBrokerRateList()),
  updateFilters: (filters: PromoCodeFilters) => dispatch(UpdatePromoCodeFilters(filters))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(PromoCodes));
